import React from 'react';
import { siteConfig } from '../data';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { fetchReferences, getItemImage, getItemSlug, optimizeImageUrl } from '../lib/cms';

export default function Home() {
  const [featuredReferences, setFeaturedReferences] = React.useState(siteConfig.references?.slice(0, 3) || []);

  React.useEffect(() => {
    let isMounted = true;
    async function loadData() {
      const res = await fetchReferences({ limit: 20 });
      if (isMounted && res && res.items && res.items.length > 0) {
        const ragelund = res.items.find(item => 
          getItemSlug(item) === 'nye-vaerelsesbygninger-raagelund-efterskole' || 
          item.title?.toLowerCase().includes('rågelund efterskole')
        );
        const withoutKamstrup = res.items.filter(item => 
          !item.title?.toLowerCase().includes('kamstrup') && 
          getItemSlug(item) !== 'kamstrup-as'
        );
        const list = [];
        if (withoutKamstrup[0]) list.push(withoutKamstrup[0]);
        if (ragelund) {
          list.push(ragelund);
        } else if (withoutKamstrup[1]) {
          list.push(withoutKamstrup[1]);
        }
        const third = withoutKamstrup.find(item => !list.some(x => getItemSlug(x) === getItemSlug(item)));
        if (third) list.push(third);

        setFeaturedReferences(list.slice(0, 3));
      }
    }
    loadData();
    return () => {
      isMounted = false;
    };
  }, []);
  return (
    <div className="animate-in fade-in duration-1000">
      {/* Hero Section */}
      <section className="relative min-h-[35vh] md:min-h-[45vh] flex items-center overflow-hidden pt-12 md:pt-16">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://assets.auxodev.dk/assets/5884feb0-c9b0-49c8-92cc-fcf945ff05d8/fb1983cb-7993-4d15-983e-75ae915ddfae.jpg" 
            alt="Kamstrup" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/70 to-transparent" />
        </div>
        
        <div className="relative z-10 max-w-5xl mx-auto px-6 w-full">
          <div className="text-white">
            <h1 className="text-3xl md:text-5xl font-serif leading-tight mb-3">
              Rådgivning inden for <br className="hidden md:block" /> byggeri.
            </h1>
            <p className="text-sm md:text-base text-white leading-relaxed font-light mb-5 max-w-lg">
              {siteConfig.description}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link 
                to="/ydelser" 
                className="bg-white text-slate-900 px-6 py-3 rounded-none font-medium hover:bg-slate-50 transition-colors"
              >
                Se ydelser
              </Link>
              <Link 
                to="/referencer" 
                className="bg-transparent border border-white text-white px-6 py-3 rounded-none font-medium hover:bg-white/10 transition-colors"
              >
                Se referencer
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Intro Text Section */}
      <section className="py-16 bg-white relative overflow-hidden">
        {/* Decorative green element */}
        <div className="absolute right-0 top-0 w-1/3 h-full bg-[#9abc5a]/70 -skew-x-12 translate-x-1/4 pointer-events-none hidden md:block" />
        
        <div className="max-w-5xl mx-auto px-6 relative z-10">
          <div className="max-w-3xl">
            <h2 className="text-2xl md:text-3xl font-serif text-slate-900 mb-6">
              Sandberg Rådgivende Ingeniører ApS
            </h2>
            <p className="text-slate-600 leading-relaxed">
              Sandberg, Rådgivende Ingeniører er et ingeniørfirma med speciale er bærende konstruktioner, projektering og tilsyn med kloakinstallationer og varmetabsrammeberegninger.
            </p>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="pt-12 pb-24 bg-slate-50">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-serif text-slate-900 mb-12 text-left break-words">Services</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {siteConfig.services.map((service, index) => (
              <div 
                key={index} 
                className={`p-6 border border-slate-100 min-h-[200px] flex flex-col justify-center transition-all duration-300 ${
                  index === 0 
                    ? 'bg-[#9abc5a] text-white border-[#9abc5a]' 
                    : 'bg-white text-slate-900 shadow-sm'
                }`}
              >
                <h3 className={`text-xl font-serif mb-4 break-words hyphens-auto ${index === 0 ? 'text-white' : 'text-slate-900'}`}>
                  {service.title}
                </h3>
                <p className={`text-sm leading-relaxed ${index === 0 ? 'text-white/90' : 'text-slate-500'}`}>
                  {service.description}
                </p>
              </div>
            ))}
          </div>
          <div className="mt-10 flex justify-start">
            <Link 
              to="/ydelser" 
              className="inline-flex items-center gap-2 border-b border-slate-900 pb-2 font-medium hover:text-slate-600 hover:border-slate-600 transition-colors text-slate-900"
            >
              Se alle ydelser
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section className="bg-white text-slate-900 overflow-hidden relative border-y border-slate-100">
        <div className="md:absolute md:inset-y-0 md:left-0 md:w-1/2 h-[400px] md:h-auto">
          <img 
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=80,format=auto/assets/5884feb0-c9b0-49c8-92cc-fcf945ff05d8/soepavillon-oedis-01.jpg" 
            alt="Søpavillon Ødis" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-5xl mx-auto px-6 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2">
            <div className="md:col-start-2 py-16 md:py-32 md:pl-16">
              <h2 className="text-2xl md:text-3xl font-serif mb-8 text-slate-900">Om os</h2>
              <div className="space-y-6 leading-relaxed text-slate-600">
                <p>
                  Sandberg Rådgivende Ingeniører er en ingeniørvirksomhed med rødder i solid faglighed og mange års erfaring inden for byggebranchen. Vi kombinerer teknisk ekspertise med en løsningsorienteret tilgang til hvert eneste projekt.
                </p>
                <p>
                  Vores mål er altid at skabe værdi for vores bygherrer gennem tæt dialog, præcis rådgivning og innovative løsninger, der holder både teknisk og økonomisk.
                </p>
              </div>
              <div className="mt-10">
                <Link 
                  to="/om-os" 
                  className="inline-flex items-center gap-2 border-b border-slate-900 pb-2 font-medium hover:text-slate-600 hover:border-slate-600 transition-colors text-slate-900"
                >
                  Læs mere
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured References */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex justify-between items-end mb-12">
            <div>
              <h2 className="text-2xl font-serif text-slate-900 mb-4">Udvalgte projekter</h2>
              <p className="text-slate-500 max-w-md">
                Vi har erfaring med en bred vifte af byggeprojekter, fra private boliger til store erhvervsbyggerier.
              </p>
            </div>
            <Link to="/referencer" className="text-slate-900 font-medium hover:underline hidden md:block">
              Se alle referencer
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {featuredReferences.map((ref, index) => {
              const slug = getItemSlug(ref);
              const rawImg = getItemImage(ref);
              const imgUrl = optimizeImageUrl(rawImg, 'width=800,quality=80,format=auto');

              return (
                <Link key={ref.id || slug || index} to={`/referencer/${slug}`} className="group block">
                  <div className="aspect-[4/3] overflow-hidden mb-4 bg-slate-100">
                    {imgUrl ? (
                      <img 
                        src={imgUrl} 
                        alt={ref.title} 
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full bg-slate-100 flex items-center justify-center text-slate-300">
                        Intet billede
                      </div>
                    )}
                  </div>
                  {ref.category && (
                    <p className="text-[10px] uppercase tracking-widest text-slate-400 mb-2">{ref.category}</p>
                  )}
                  <h3 className="text-xl font-serif text-slate-900 mb-2">{ref.title}</h3>
                  {ref.description && (
                    <p className="text-slate-500 text-sm leading-relaxed">{ref.description}</p>
                  )}
                </Link>
              );
            })}
          </div>
          
          <div className="mt-12 md:hidden">
            <Link to="/referencer" className="inline-flex items-center gap-2 text-slate-900 font-medium">
              Se alle referencer
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
